import { reaction } from 'mobx'
import { type EditorView } from 'prosemirror-view'
import { getCollabItemWidgetState } from './constants'
import { State } from './Types'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from 'react-query'
import reduxStore from 'redux/store'
import { CollabItemInlineWidget } from 'components/CollabItemInlineWidget/CollabItemInlineWidget'
import { createRoot, Root } from 'react-dom/client'

export class CollabItemWidgetView {
  view: EditorView
  queryClient: QueryClient
  disposers: Array<() => void>
  widgets: { [key: string]: Root } = {}

  constructor(view: EditorView) {
    this.view = view

    const { store, queryClient }: State = getCollabItemWidgetState(this.view.state)
    this.queryClient = queryClient
    this.disposers = [
      reaction(
        () => [store.selectedUuid, store.highlightedUuid],
        (values, prevValues) => {
          const [selectedItemUuid, highlightedItemUuid] = values
          const [previouslySelectedItemUuid, previouslyHighlightedItemUuid] = prevValues

          if (previouslyHighlightedItemUuid !== highlightedItemUuid) {
            if (previouslyHighlightedItemUuid) {
              this.widgets[previouslyHighlightedItemUuid].render(<></>)
            }
            if (highlightedItemUuid) {
              this.renderWidget(highlightedItemUuid)
            }
          }
          if (previouslySelectedItemUuid !== selectedItemUuid) {
            if (previouslySelectedItemUuid) {
              this.widgets[previouslySelectedItemUuid].render(<></>)
            }
            if (selectedItemUuid) {
              this.renderWidget(selectedItemUuid, true)
            }
          }
          this.view.dispatch(this.view.state.tr)
        },
      ),
    ]
  }

  renderWidget(uuid: string, scrollIntoView: boolean = false) {
    if (!this.view.dom.parentNode) {
      return
    }

    const { store }: State = getCollabItemWidgetState(this.view.state)
    const anchor = this.getAnchor(uuid)
    if (!anchor) {
      return
    }

    if (!this.widgets[uuid]) {
      const container = document.createElement('div')
      this.widgets[uuid] = createRoot(container)
      this.view.dom.parentNode.appendChild(container)
    }

    this.widgets[uuid].render(
      <Provider store={reduxStore}>
        <QueryClientProvider client={this.queryClient}>
          <CollabItemInlineWidget anchor={anchor} uuid={uuid} store={store} />
        </QueryClientProvider>
      </Provider>,
    )

    if (scrollIntoView) {
      anchor.scrollIntoViewIfNeeded()
    }
  }

  getAnchor(uuid: string) {
    let anchor = this.view.dom.querySelector(`[data-uuid='${uuid}']:not(h1, h2, h3, h4, h5, p)`)
    if (!anchor) {
      anchor = this.view.dom.querySelector(`[data-uuid='${uuid}']`)
    }
    return anchor
  }

  update(view: EditorView) {
    this.view = view
    const { store } = getCollabItemWidgetState(this.view.state)
    store.setView(this.view)
  }

  destroy() {
    const { store } = getCollabItemWidgetState(this.view.state)
    store.setView(undefined)
    this.disposers.forEach((dispose) => dispose())
  }
}
